/**
 * BlockSubtypeManagerDialog — project block-subtype manager (UXR C2/C6').
 *
 * Master-detail dialog opened from the Block Templates panel gear (the ONLY
 * entry — Q1). Hosts the catalog CRUD form previously shipped as
 * BlockSubtypeDrawer, upgraded per the approved mocks:
 * - multiline instruction bodies (Q6)
 * - slug auto-fill from name (editable until first save, then immutable)
 * - in-place strategist/tactician consumption guidance
 * - user-facing "Screen examples" section (C6', level = binding strictness)
 * - Delete with the usageCount guard, empty-catalog state
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { StrategyBlockInstruction } from '@segmently/schema-models/strategy';
import type {
  BlockSubtype,
  BlockSubtypeInput,
  BlockSubtypeScreenExample,
} from '@modules/firebase-core';
import {
  SDialog,
  SortableList,
  StyledSelectDropdown,
  StyledTextArea,
  StyledTextField,
} from '@modules/ui-components';
import type { UseBlockSubtypesResult } from '../hooks/useBlockSubtypes';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';

const EMPTY_FORM: BlockSubtypeInput = {
  name: '',
  slug: '',
  description: '',
  playbook: '',
  defaultInstructions: [],
  screenExamples: [],
  enabled: true,
};

const INSTRUCTION_LEVELS = [
  'mustHave',
  'should',
  'niceToHave',
  'forbidden',
] as const;

const EXAMPLE_LEVELS = ['mustHave', 'should', 'niceToHave'] as const;

const EXAMPLE_LEVEL_HINT: Record<(typeof EXAMPLE_LEVELS)[number], string> = {
  mustHave: 'follow the structure exactly',
  should: 'reproduce the pattern, write your own content',
  niceToHave: 'inspiration only',
};

/** Mirror of the shared slug contract: lowercase, [a-z0-9_-], ≤80 chars. */
function slugifyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}

export interface BlockSubtypeManagerDialogProps {
  open: boolean;
  onClose: () => void;
  catalog: UseBlockSubtypesResult;
}

/** Project-owned custom block subtype CRUD, opened from the templates panel gear. */
export const BlockSubtypeManagerDialog: React.FC<BlockSubtypeManagerDialogProps> = ({
  open,
  onClose,
  catalog,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<BlockSubtype | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<BlockSubtypeInput>(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = useMemo(
    () => catalog.subtypes.find((item) => item.id === selectedId)
      ?? (selectedSnapshot?.id === selectedId ? selectedSnapshot : null),
    [catalog.subtypes, selectedId, selectedSnapshot],
  );

  const load = useCallback((item: BlockSubtype | null) => {
    setSelectedId(item?.id ?? null);
    setSelectedSnapshot(item);
    setCreating(item === null);
    setSlugTouched(Boolean(item));
    setForm(item ? {
      name: item.name,
      slug: item.slug,
      description: item.description,
      playbook: item.playbook,
      defaultInstructions: item.defaultInstructions.map((instruction) => ({ ...instruction })),
      screenExamples: (item.screenExamples ?? []).map((example) => ({ ...example })),
      enabled: item.enabled,
    } : { ...EMPTY_FORM, defaultInstructions: [], screenExamples: [] });
    setError(null);
  }, []);

  useEffect(() => {
    if (!open || selectedId || creating || catalog.loading || catalog.subtypes.length === 0) return;
    load(catalog.subtypes[0]);
  }, [catalog.loading, catalog.subtypes, creating, load, open, selectedId]);

  const setField = useCallback(<K extends keyof BlockSubtypeInput>(key: K, value: BlockSubtypeInput[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError(null);
  }, []);

  // Slug auto-fills from the name until the user edits it (create mode only).
  // Read creating/slugTouched via refs: StyledTextField is memoized with a
  // comparator that ignores onChange, so a closure over these states goes
  // stale when they flip after the field's first render.
  const creatingRef = useRef(creating);
  creatingRef.current = creating;
  const slugTouchedRef = useRef(slugTouched);
  slugTouchedRef.current = slugTouched;
  const handleNameChange = useCallback((value: string) => {
    setForm((current) => ({
      ...current,
      name: value,
      ...(!slugTouchedRef.current && creatingRef.current ? { slug: slugifyName(value) } : {}),
    }));
    setError(null);
  }, []);

  const handleSlugChange = useCallback((value: string) => {
    setSlugTouched(true);
    setField('slug', value);
  }, [setField]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = selected
        ? await catalog.updateSubtype(selected.id, form)
        : await catalog.createSubtype(form);
      load(saved);
    } catch (nextError) {
      setError((nextError as Error).message);
    } finally {
      setSaving(false);
    }
  }, [catalog, form, load, selected]);

  const usageGuard = (selected?.usageCount ?? 0) > 0;

  const handleDelete = useCallback(async () => {
    if (!selected) return;
    if ((selected.usageCount ?? 0) > 0) {
      setError('Disable this subtype instead. Assigned blocks must be migrated before deletion.');
      return;
    }
    if (!window.confirm(`Delete subtype "${selected.name}"? This cannot be undone.`)) return;
    setSaving(true);
    setError(null);
    try {
      await catalog.deleteSubtype(selected.id);
      const next = catalog.subtypes.find((item) => item.id !== selected.id) ?? null;
      load(next);
    } catch (nextError) {
      setError((nextError as Error).message);
    } finally {
      setSaving(false);
    }
  }, [catalog, load, selected]);

  const addInstruction = useCallback(() => {
    setField('defaultInstructions', [
      ...form.defaultInstructions,
      { id: crypto.randomUUID(), level: 'should', text: '' },
    ]);
  }, [form.defaultInstructions, setField]);

  const updateInstruction = useCallback((
    index: number,
    patch: Partial<StrategyBlockInstruction>,
  ) => {
    setField('defaultInstructions', form.defaultInstructions.map((instruction, instructionIndex) => (
      instructionIndex === index ? { ...instruction, ...patch } : instruction
    )));
  }, [form.defaultInstructions, setField]);

  const removeInstruction = useCallback((index: number) => {
    setField('defaultInstructions', form.defaultInstructions.filter((_, instructionIndex) => instructionIndex !== index));
  }, [form.defaultInstructions, setField]);

  const addExample = useCallback(() => {
    setField('screenExamples', [
      ...form.screenExamples,
      { level: 'should', text: '' },
    ]);
  }, [form.screenExamples, setField]);

  const updateExample = useCallback((
    index: number,
    patch: Partial<BlockSubtypeScreenExample>,
  ) => {
    setField('screenExamples', form.screenExamples.map((example, exampleIndex) => (
      exampleIndex === index ? { ...example, ...patch } : example
    )));
  }, [form.screenExamples, setField]);

  const removeExample = useCallback((index: number) => {
    setField('screenExamples', form.screenExamples.filter((_, exampleIndex) => exampleIndex !== index));
  }, [form.screenExamples, setField]);

  const emptyCatalog = !catalog.loading && catalog.subtypes.length === 0 && !creating;

  return (
    <SDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      title="Block subtypes"
      data-testid={STRATEGY_CANVAS.subtypeManagerDialog}
      actions={selected ? [{
        label: 'Delete',
        onClick: () => void handleDelete(),
        variant: 'text',
        color: 'error',
        disabled: saving || usageGuard,
        startIcon: <DeleteOutlineIcon />,
        'data-testid': STRATEGY_CANVAS.subtypeDelete,
      }] : undefined}
      primaryAction={emptyCatalog ? undefined : {
        label: saving ? 'Saving…' : selected ? 'Save changes' : 'Create subtype',
        onClick: () => void handleSave(),
        disabled: saving,
        'data-testid': STRATEGY_CANVAS.subtypeSave,
      }}
    >
      <Stack spacing={1.5} sx={{ minHeight: 480 }}>
        {/* Consumption guidance: who reads what (mock strip) */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          data-testid={STRATEGY_CANVAS.subtypeGuidance}
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            🧭 <b>Strategist</b> sees name + description and assigns the subtype by exact slug
          </Typography>
          <Typography variant="caption" color="text.secondary">
            🛠 <b>Tactician</b> loads the playbook, instructions and screen examples when building this block's screens
          </Typography>
          <Typography variant="caption" color="text.secondary">
            🧱 Enabled subtypes appear as cards in <b>Block Templates</b>
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
          <Chip size="small" label={`${catalog.subtypes.length} / 30`} variant="outlined" />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => load(null)}
            data-testid={STRATEGY_CANVAS.subtypeNew}
          >
            New subtype
          </Button>
        </Stack>

        {emptyCatalog ? (
          <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 8 }}>
            <Typography variant="h6">No subtypes yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, textAlign: 'center' }}>
              A subtype packages a project-specific block pattern: a strategist-facing description,
              a tactician playbook, default instructions and screen examples. Create the first one —
              it appears as a card in Block Templates.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => load(null)}>
              New subtype
            </Button>
          </Stack>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, minHeight: 0, flex: 1 }}>
            <Box sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider', overflowY: 'auto' }}>
              {catalog.loading ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading catalog…</Typography>
              ) : (
                <List disablePadding>
                  {catalog.subtypes.map((item) => (
                    <ListItemButton
                      key={item.id}
                      selected={item.id === selectedId}
                      onClick={() => load(item)}
                      data-testid={STRATEGY_CANVAS.subtypeRow(item.id)}
                    >
                      <ListItemText
                        primary={item.name}
                        secondary={`${item.slug} · ${item.usageCount ?? 0} uses`}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                      {!item.enabled && <Chip size="small" label="Disabled" />}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>

            <Stack spacing={2} sx={{ p: 2.5, overflowY: 'auto' }}>
              {(error || catalog.error) && <Alert severity="error">{error || catalog.error?.message}</Alert>}
              <StyledTextField
                label="Name"
                value={form.name}
                onChange={handleNameChange}
                required
                maxLength={160}
                helperText="Shown on cards and canvas badges"
                data-testid={STRATEGY_CANVAS.subtypeName}
              />
              <StyledTextField
                label="Slug"
                value={form.slug}
                onChange={handleSlugChange}
                required
                disabled={Boolean(selected)}
                maxLength={80}
                placeholder="proof_explorer"
                helperText={selected
                  ? 'Immutable after the first save — the strategist binds by this exact slug'
                  : 'Auto-generated from name · editable until first save, then immutable'}
                data-testid={STRATEGY_CANVAS.subtypeSlug}
              />
              <StyledTextArea
                label="Description"
                value={form.description}
                onChange={(value) => setField('description', value)}
                maxLength={1000}
                showCharacterCount
                minRows={2}
                helperText="One line the strategist reads when deciding to assign this subtype"
                data-testid={STRATEGY_CANVAS.subtypeDescription}
              />
              <StyledTextArea
                label="Playbook (markdown)"
                value={form.playbook}
                onChange={(value) => setField('playbook', value)}
                maxLength={8000}
                showCharacterCount
                minRows={7}
                helperText="Becomes the tactician's system prompt for this subtype (falls back to the global custom playbook)"
                data-testid={STRATEGY_CANVAS.subtypePlaybook}
              />
              <FormControlLabel
                control={(
                  <Switch
                    data-testid={STRATEGY_CANVAS.subtypeEnabled}
                    checked={form.enabled}
                    onChange={(_, checked) => setField('enabled', checked)}
                  />
                )}
                label="Enabled for new block assignments"
              />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Default instructions</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Copied into a block when the subtype is assigned. mustHave and forbidden are
                    enforced by the reviewer; should = strong preference; niceToHave = optional.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addInstruction}
                  disabled={form.defaultInstructions.length >= 40}
                  data-testid={STRATEGY_CANVAS.subtypeInstructionAdd}
                >
                  Add
                </Button>
              </Stack>

              {form.defaultInstructions.length > 0 ? (
                <SortableList
                  items={form.defaultInstructions}
                  onReorder={(items) => setField('defaultInstructions', items)}
                  data-testid={STRATEGY_CANVAS.subtypeInstructionList}
                >
                  {(instruction, index) => (
                    <SortableList.Item
                      showDragHandle
                      data-testid={STRATEGY_CANVAS.subtypeInstruction(index)}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
                        <Box sx={{ width: 150, flexShrink: 0 }}>
                          <StyledSelectDropdown
                            label="Level"
                            value={instruction.level}
                            onChange={(value) => updateInstruction(index, { level: value as StrategyBlockInstruction['level'] })}
                            options={INSTRUCTION_LEVELS.map((level) => ({ value: level, label: level }))}
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <StyledTextArea
                            label="Instruction"
                            value={instruction.text}
                            onChange={(value) => updateInstruction(index, { text: value })}
                            minRows={2}
                            fullWidth
                          />
                        </Box>
                        <Tooltip title="Remove instruction">
                          <IconButton aria-label="Remove instruction" onClick={() => removeInstruction(index)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </SortableList.Item>
                  )}
                </SortableList>
              ) : (
                <Alert severity="info" variant="outlined">No default instructions. The playbook still applies.</Alert>
              )}

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2">Screen examples</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Read by the tactician when generating screens for this subtype. Level = binding
                    strictness: mustHave — {EXAMPLE_LEVEL_HINT.mustHave} · should — {EXAMPLE_LEVEL_HINT.should} ·
                    niceToHave — {EXAMPLE_LEVEL_HINT.niceToHave}. Markdown / JSON / plain structure notes welcome.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addExample}
                  disabled={form.screenExamples.length >= 10}
                  data-testid={STRATEGY_CANVAS.subtypeExampleAdd}
                >
                  Add example
                </Button>
              </Stack>

              {form.screenExamples.length > 0 ? (
                <Stack spacing={1} data-testid={STRATEGY_CANVAS.subtypeExampleList}>
                  {form.screenExamples.map((example, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      data-testid={STRATEGY_CANVAS.subtypeExample(index)}
                    >
                      <Box sx={{ width: 150, flexShrink: 0 }}>
                        <StyledSelectDropdown
                          label="Binding"
                          value={example.level}
                          onChange={(value) => updateExample(index, { level: value as BlockSubtypeScreenExample['level'] })}
                          options={EXAMPLE_LEVELS.map((level) => ({ value: level, label: level }))}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <StyledTextArea
                          label="Example"
                          value={example.text}
                          onChange={(value) => updateExample(index, { text: value })}
                          maxLength={2000}
                          showCharacterCount
                          minRows={3}
                          fullWidth
                        />
                      </Box>
                      <Tooltip title="Remove example">
                        <IconButton aria-label="Remove example" onClick={() => removeExample(index)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Alert severity="info" variant="outlined">
                  No screen examples. The tactician relies on the playbook and global example packs.
                </Alert>
              )}

              {usageGuard && (
                <Typography variant="caption" color="text.secondary">
                  Used by {selected?.usageCount} block{(selected?.usageCount ?? 0) === 1 ? '' : 's'} — disable it
                  instead, or detach the blocks first. Examples are project-local; they never change global prompt packs.
                </Typography>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </SDialog>
  );
};
